import { FC, useMemo } from 'react';

import { CampaignType } from 'modules/domains/campaign';
import { InAppContentDefinition, InAppDestination } from 'modules/graphql';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';

import {
  aggregateErrors,
  getCorrectlyCasedLocale,
  getNBALocalesErrors,
  getOtherLocaleError,
} from '../../common/utils';
import { useCampaignQueryContext } from '../CampaignQueryContext';
import { ErrorValidationContext } from '../ErrorValidationContext';
import { useQueryParamContext } from '../QueryParamContext';
import { useTemplateQueryContext } from '../TemplateQueryContext';
import { useLocaleErrorCollector } from './useLocaleErrorCollector';

export const ErrorValidationContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { template } = useTemplateQueryContext();
  const { campaign } = useCampaignQueryContext();

  const { campaignTypeDefinition, lists } = campaign;
  const { locale: preParsedLocale } = useQueryParamContext();
  const liveTemplate = JSON.parse(template);
  const { templateType } = liveTemplate;

  const { activeTemplateLocales, allProjectLocales, templateDefaultLocale } =
    useLocalesQueryContext();

  const templateLocaleTags = activeTemplateLocales.map((l) => l.tag);
  const defaultLocaleTag = templateDefaultLocale.tag || '';
  const currentLocaleTag =
    preParsedLocale === ''
      ? defaultLocaleTag
      : getCorrectlyCasedLocale(preParsedLocale, allProjectLocales);
  // This contains a list of locale names that have errors
  const inAppMediumData = liveTemplate.mediumData as InAppContentDefinition;
  const hasRokuDestination = inAppMediumData?.destinations?.includes(InAppDestination.Roku);
  const hasRokuFields = !!inAppMediumData?.ottPayload;

  /**
   * Since we don't set destinations for a template with "RokuTemplating" FF on until after the template has been created,
   * we need to set isSendable to false if the front-end has a roku destination but no roku content.
   */
  const canSendRokuCampaign = hasRokuDestination ? hasRokuFields : true;

  const {
    allContentErrors,
    validationFailures,
    isFinishedValidating,
    clearLocaleValidation,
    revalidateLocales,
    revalidateLocale,
  } = useLocaleErrorCollector(currentLocaleTag, defaultLocaleTag, templateLocaleTags);

  const {
    designContentErrors,
    designContentWarnings,
    settingsLocaleContentErrors,
    settingsLocaleContentWarnings,
  } = aggregateErrors(currentLocaleTag, validationFailures, templateType);

  // Create some primitives for useEffect & useMemo
  const primitiveAllContentErrors = JSON.stringify(allContentErrors);

  const handleRevalidation = (localeTag?: string) => {
    if (localeTag === undefined) {
      revalidateLocales();
    } else {
      // if we don't need to check allProjectLocales, maybe we can just check the active templates
      const locale = localeTag
        ? getCorrectlyCasedLocale(localeTag, allProjectLocales)
        : defaultLocaleTag;

      revalidateLocale(locale);
    }
  };

  const removeValidation = (localeTag: string) => {
    clearLocaleValidation(localeTag);
  };

  // move all this into the hook so we can test it.
  const hasOtherLocaleError = useMemo(() => {
    const aryAllContentErrors = JSON.parse(primitiveAllContentErrors);
    return getOtherLocaleError(currentLocaleTag, aryAllContentErrors);
  }, [currentLocaleTag, primitiveAllContentErrors]);

  const isSendable = useMemo(() => {
    const aryAllContentErrors = JSON.parse(primitiveAllContentErrors);
    return isFinishedValidating && !aryAllContentErrors.length && canSendRokuCampaign;
  }, [primitiveAllContentErrors, isFinishedValidating, canSendRokuCampaign]);

  // end move

  const isBlastMissingAudience = useMemo(
    () => campaignTypeDefinition === CampaignType.Blast && !lists?.length,
    [campaignTypeDefinition, lists?.length],
  );

  return (
    <ErrorValidationContext.Provider
      value={{
        allContentErrors,
        designContentErrors,
        designContentWarnings,
        hasOtherLocaleError,
        hasValidationError: isFinishedValidating && !!allContentErrors.length,
        isBlastMissingAudience,
        isFinishedValidating,
        isSendable,
        nbaLocalesErrors: getNBALocalesErrors(liveTemplate?.name, templateLocaleTags),
        localeContentErrors: settingsLocaleContentErrors,
        localeContentWarnings: settingsLocaleContentWarnings,
        revalidate: handleRevalidation,
        removeValidation,
      }}
    >
      {children}
    </ErrorValidationContext.Provider>
  );
};
